import React, { useState, useEffect, useCallback } from "react";
import FullAccessDemoService from "../../services/FullAccessDemoService";
import toast from "react-hot-toast";
import { useTranslation } from "../../i18n/LanguageContext";
import { parseToLocalDate } from "./utils/DateTimeUtil";
import DateTimeField from "../common/datetime/DateTimeField";
import {
  browserTimeZone,
  instantToWallClock,
  nowWallClock,
  wallClockToIso,
} from "../common/datetime/dateTimeParts";

/** Seat windows are set in whatever zone the admin is sitting in. */
const TZ = browserTimeZone();

/**
 * Admin panel for managing full-access event demo seats.
 * Accessible via Platform Admin > Demo > Event Demo Seats in the sidebar.
 */
const FullAccessDemoAdmin = () => {
  const { t } = useTranslation();
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("seats");
  const [eventFilter, setEventFilter] = useState("ALL");
  const [actionLoading, setActionLoading] = useState(null);

  // Inline action state — tracks which seat has an open action form
  const [inlineAction, setInlineAction] = useState(null); // { seatCode, type: 'extend'|'reset-password', value: '' }

  // Create demo user form state
  const [createForm, setCreateForm] = useState({
    eventName: "",
    startsAt: "",
    expiresAt: "",
  });
  const [createLoading, setCreateLoading] = useState(false);

  const loadSeats = useCallback(async () => {
    setLoading(true);
    try {
      const result = await FullAccessDemoService.listSeats();
      // This panel manages EVENT_DEMO seats only. Non-event test-user seats
      // (e.g. EXTENSION_REVIEW) have their own Test Users panel.
      if (result?.data)
        setSeats(
          result.data.filter((s) => !s.seatType || s.seatType === "EVENT_DEMO"),
        );
    } catch {
      toast.error(t("fullAccessDemo.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSeats();
  }, [loadSeats]);

  // --- Per-seat actions ---
  const handleDeactivate = async (seatCode) => {
    setActionLoading(`deactivate-${seatCode}`);
    try {
      const result = await FullAccessDemoService.deactivateSeat(seatCode);
      if (result?.success) {
        toast.success(t("fullAccessDemo.toast.deactivated"));
        loadSeats();
      } else {
        toast.error(result?.message || "Deactivate failed");
      }
    } catch {
      toast.error(t("fullAccessDemo.toast.deactivateFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleReactivate = async (seat) => {
    const { seatCode, startsAt, expiresAt } = seat;
    const now = new Date();
    if (startsAt && parseToLocalDate(startsAt) > now) {
      toast.error(
        `Cannot reactivate — this seat does not start until ${formatDt(startsAt)}. Extend or update the start time first.`,
      );
      return;
    }
    if (expiresAt && parseToLocalDate(expiresAt) <= now) {
      toast.error(
        `Cannot reactivate — this seat expired on ${formatDt(expiresAt)}. Extend the expiry first.`,
      );
      return;
    }
    setActionLoading(`reactivate-${seatCode}`);
    try {
      const result = await FullAccessDemoService.reactivateSeat(seatCode);
      if (result?.success) {
        toast.success(t("fullAccessDemo.toast.reactivated"));
        loadSeats();
      } else {
        toast.error(result?.message || "Reactivate failed");
      }
    } catch {
      toast.error(t("fullAccessDemo.toast.reactivateFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleExtend = async (seatCode, newExpiry) => {
    if (!newExpiry) {
      toast.error(t("fullAccessDemo.toast.selectExpiry"));
      return;
    }
    setActionLoading(`extend-${seatCode}`);
    try {
      const result = await FullAccessDemoService.extendSeat(
        seatCode,
        wallClockToIso(newExpiry, TZ),
      );
      if (result?.success) {
        toast.success(t("fullAccessDemo.toast.extended"));
        setInlineAction(null);
        loadSeats();
      } else {
        toast.error(result?.message || "Extend failed");
      }
    } catch {
      toast.error(t("fullAccessDemo.toast.extendFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPassword = async (seatCode, newPassword) => {
    if (!newPassword || newPassword.length < 12) {
      toast.error(
        'Password must be at least 12 characters. Try a passphrase like "coffee-morning-sunshine-blue".',
      );
      return;
    }
    setActionLoading(`reset-${seatCode}`);
    try {
      const result = await FullAccessDemoService.resetPassword(
        seatCode,
        newPassword,
      );
      if (result?.success) {
        toast.success(t("fullAccessDemo.toast.passwordUpdated"));
        setInlineAction(null);
        loadSeats();
      } else {
        toast.error(result?.message || "Reset failed");
      }
    } catch {
      toast.error(t("fullAccessDemo.toast.passwordResetFailed"));
    } finally {
      setActionLoading(null);
    }
  };

  // --- Create single demo user ---
  const handleCreateUser = async (e) => {
    e.preventDefault();
    const { eventName, startsAt, expiresAt } = createForm;
    if (!expiresAt) {
      toast.error(t("fullAccessDemo.toast.expiryRequired"));
      return;
    }
    if (startsAt && expiresAt <= startsAt) {
      toast.error(t("fullAccessDemo.toast.expiryAfterStart"));
      return;
    }
    setCreateLoading(true);
    try {
      const result = await FullAccessDemoService.createSeat(
        eventName.trim() || "Demo Users",
        wallClockToIso(expiresAt, TZ),
        startsAt ? wallClockToIso(startsAt, TZ) : null,
      );
      if (result?.success) {
        toast.success(result.message || "Demo user created");
        setCreateForm({
          eventName: "",
          startsAt: "",
          expiresAt: "",
        });
        setActiveTab("seats");
        loadSeats();
      } else {
        toast.error(result?.message || "Create failed");
      }
    } catch {
      toast.error(t("fullAccessDemo.toast.createFailed"));
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Derived data ---
  const eventNames = Array.from(
    new Set(seats.map((s) => s.eventName).filter(Boolean)),
  ).sort();
  const filteredSeats =
    eventFilter === "ALL"
      ? seats
      : seats.filter((s) => s.eventName === eventFilter);

  const stats = {
    active: seats.filter((s) => s.status === "ACTIVE").length,
    deactivated: seats.filter((s) => s.status === "DEACTIVATED").length,
    expired: seats.filter((s) => s.status === "EXPIRED").length,
    total: seats.length,
  };

  // --- Styles (matching DemoAdminPanel pattern) ---
  const containerStyle = {
    padding: "12px 16px",
    color: "var(--text-primary)",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  };

  const cardStyle = {
    background: "var(--bg-card)",
    border: "1px solid var(--border-color)",
    borderRadius: "10px",
    padding: "12px",
    marginBottom: "12px",
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: "14px",
  };

  const thStyle = {
    textAlign: "left",
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-secondary)",
    fontWeight: 600,
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  };

  const tdStyle = {
    padding: "8px 10px",
    borderBottom: "1px solid var(--border-color)",
    color: "var(--text-primary)",
    verticalAlign: "top",
  };

  const tabStyle = (active) => ({
    padding: "8px 14px",
    background: active ? "#2196f3" : "transparent",
    color: active ? "#fff" : "var(--text-secondary)",
    border: "1px solid " + (active ? "#2196f3" : "var(--border-color)"),
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: active ? 600 : 400,
    marginRight: "8px",
  });

  const btnStyle = (variant = "primary", small = false) => ({
    padding: small ? "4px 10px" : "6px 14px",
    borderRadius: "6px",
    border: "none",
    cursor: "pointer",
    fontSize: small ? "12px" : "13px",
    fontWeight: 500,
    ...(variant === "primary"
      ? { background: "#4caf50", color: "#fff" }
      : variant === "danger"
        ? { background: "#f44336", color: "#fff" }
        : variant === "warning"
          ? { background: "#f57c00", color: "#fff" }
          : variant === "blue"
            ? { background: "#2196f3", color: "#fff" }
            : {
                background: "var(--bg-primary)",
                color: "var(--text-secondary)",
                border: "1px solid var(--border-color)",
              }),
  });

  const inputStyle = {
    padding: "6px 10px",
    background: "var(--bg-input, var(--bg-primary))",
    border: "1px solid var(--border-color)",
    borderRadius: "6px",
    color: "var(--text-primary)",
    fontSize: "13px",
  };

  const getStatusBadge = (status) => {
    const colours = {
      ACTIVE: { bg: "#4caf50", color: "#fff" },
      DEACTIVATED: { bg: "#f57c00", color: "#fff" },
      EXPIRED: { bg: "#616161", color: "#ccc" },
    };
    const labels = {};
    const c = colours[status] || { bg: "#333", color: "#aaa" };
    return (
      <span
        style={{
          padding: "3px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 600,
          background: c.bg,
          color: c.color,
          display: "inline-block",
        }}
      >
        {labels[status] || status}
      </span>
    );
  };

  // Backend serialises LocalDateTime without a zone suffix; parseToLocalDate treats it as UTC.
  const formatDt = (str) => {
    const d = parseToLocalDate(str);
    if (!d) return "—";
    return (
      d.toLocaleDateString() +
      " " +
      d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
    );
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <h2>Event Demo Seats</h2>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
        }}
      >
        <h2 style={{ margin: 0, color: "var(--text-primary)" }}>
          Event Demo Seat Management
        </h2>
        <button onClick={loadSeats} style={btnStyle("secondary")}>
          🔄 Refresh
        </button>
      </div>

      {/* Security + Operations reminder */}
      <div
        style={{
          ...cardStyle,
          borderLeft: "4px solid #f57c00",
          background: "var(--bg-card)",
        }}
      >
        <div
          style={{
            fontWeight: 600,
            color: "var(--text-primary)",
            marginBottom: "6px",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          🔐 Security &amp; Operations
        </div>
        <ul
          style={{
            margin: 0,
            paddingLeft: "20px",
            color: "var(--text-secondary)",
            fontSize: "13px",
            lineHeight: 1.6,
          }}
        >
          <li>
            Use strong generated passwords and rotate before each event day.
          </li>
          <li>
            Add audit logs for: seat activation, password reset, revoke, extend.
          </li>
          <li>Add emergency kill switch: pool pause/revoke all.</li>
          <li>
            Consider post-event cleanup job:
            <ul style={{ marginTop: "2px", paddingLeft: "20px" }}>
              <li>deactivate seats</li>
              <li>expire subscriptions</li>
              <li>
                archive/delete transient demo artifacts as policy requires
              </li>
            </ul>
          </li>
        </ul>
      </div>

      {/* Stats */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "12px",
          flexWrap: "wrap",
        }}
      >
        {[
          { label: "Active", count: stats.active, color: "#4caf50" },
          { label: "Deactivated", count: stats.deactivated, color: "#f57c00" },
          { label: "Expired", count: stats.expired, color: "#616161" },
          { label: "Total", count: stats.total, color: "#2196f3" },
        ].map((s) => (
          <div
            key={s.label}
            style={{
              ...cardStyle,
              flex: "1",
              minWidth: "90px",
              textAlign: "center",
              marginBottom: 0,
              padding: "10px 8px",
            }}
          >
            <div style={{ fontSize: "28px", fontWeight: 700, color: s.color }}>
              {s.count}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-secondary)",
                marginTop: "2px",
              }}
            >
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: "12px" }}>
        <button
          style={tabStyle(activeTab === "seats")}
          onClick={() => setActiveTab("seats")}
        >
          Seats ({seats.length})
        </button>
        <button
          style={tabStyle(activeTab === "create")}
          onClick={() => setActiveTab("create")}
        >
          + Create User
        </button>
      </div>

      {/* ─── CREATE USER TAB ─── */}
      {activeTab === "create" && (
        <div style={cardStyle}>
          <h3 style={{ margin: "0 0 12px", fontSize: "15px" }}>
            Create a Demo User
          </h3>
          <form onSubmit={handleCreateUser}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "12px",
                marginBottom: "14px",
              }}
            >
              <label
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  fontSize: "13px",
                  color: "var(--text-secondary)",
                }}
              >
                Label (optional)
                <input
                  type="text"
                  placeholder="e.g. KinShield Summit 2026"
                  value={createForm.eventName}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, eventName: e.target.value }))
                  }
                  style={{ ...inputStyle }}
                />
              </label>
              <DateTimeField
                label="Starts at"
                value={createForm.startsAt}
                hint="Leave empty to start the seat immediately."
                allowClear
                presets={["now", "+1d", "+7d"]}
                onChange={(value) =>
                  setCreateForm((f) => ({ ...f, startsAt: value }))
                }
              />
              <DateTimeField
                label="Expires at"
                required
                value={createForm.expiresAt}
                min={createForm.startsAt || nowWallClock(TZ)}
                minMessage={createForm.startsAt ? "the start time" : "now"}
                presetBase={createForm.startsAt}
                presets={["+4h", "+1d", "+3d", "+7d"]}
                onChange={(value) =>
                  setCreateForm((f) => ({ ...f, expiresAt: value }))
                }
              />
            </div>
            <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
              <button
                type="submit"
                disabled={createLoading}
                style={btnStyle("primary")}
              >
                {createLoading ? "Creating..." : "🚀 Create User"}
              </button>
              <span
                style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
                Creates the next demo-user-N seat (ACTIVE & usable immediately).
              </span>
            </div>
          </form>
        </div>
      )}

      {/* ─── SEATS TAB ─── */}
      {activeTab === "seats" && (
        <>
          {/* Filter bar */}
          <div
            style={{
              ...cardStyle,
              display: "flex",
              gap: "10px",
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              style={{ ...inputStyle, minWidth: "220px" }}
            >
              <option value="ALL">All Events</option>
              {eventNames.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>

          {/* Seats table */}
          <div style={cardStyle}>
            {filteredSeats.length === 0 ? (
              <p
                style={{ textAlign: "center", color: "#667", padding: "20px" }}
              >
                {seats.length === 0
                  ? "No demo seats yet — create a demo user to get started"
                  : "No seats match the selected event"}
              </p>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Seat Code</th>
                      <th style={thStyle}>Event</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Email Fwd</th>
                      <th style={thStyle}>SMS Fwd</th>
                      <th style={thStyle}>Starts</th>
                      <th style={thStyle}>Expires</th>
                      <th style={thStyle}>Pwd Rotated</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSeats.map((seat) => {
                      const isOpen = inlineAction?.seatCode === seat.seatCode;
                      return (
                        <React.Fragment key={seat.seatCode}>
                          <tr>
                            <td
                              style={{
                                ...tdStyle,
                                fontFamily: "monospace",
                                fontSize: "13px",
                              }}
                            >
                              {seat.seatCode}
                            </td>
                            <td style={tdStyle}>{seat.eventName}</td>
                            <td style={tdStyle}>
                              {getStatusBadge(seat.status)}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              {seat.canForwardEmail ? "✅" : "❌"}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              {seat.canForwardSms ? "✅" : "❌"}
                            </td>
                            <td style={{ ...tdStyle, fontSize: "12px" }}>
                              {formatDt(seat.startsAt)}
                            </td>
                            <td
                              style={{
                                ...tdStyle,
                                fontSize: "12px",
                                color:
                                  parseToLocalDate(seat.expiresAt) < new Date()
                                    ? "#f44336"
                                    : "inherit",
                              }}
                            >
                              {formatDt(seat.expiresAt)}
                            </td>
                            <td style={{ ...tdStyle, fontSize: "12px" }}>
                              {formatDt(seat.passwordRotatedAt)}
                            </td>
                            <td style={tdStyle}>
                              <div
                                style={{
                                  display: "flex",
                                  gap: "4px",
                                  flexWrap: "wrap",
                                }}
                              >
                                {seat.status === "ACTIVE" ? (
                                  <button
                                    onClick={() =>
                                      handleDeactivate(seat.seatCode)
                                    }
                                    disabled={
                                      actionLoading ===
                                      `deactivate-${seat.seatCode}`
                                    }
                                    style={btnStyle("warning", true)}
                                  >
                                    {actionLoading ===
                                    `deactivate-${seat.seatCode}`
                                      ? "..."
                                      : "Deactivate"}
                                  </button>
                                ) : (
                                  seat.status !== "EXPIRED" && (
                                    <button
                                      onClick={() => handleReactivate(seat)}
                                      disabled={
                                        actionLoading ===
                                        `reactivate-${seat.seatCode}`
                                      }
                                      style={btnStyle("primary", true)}
                                    >
                                      {actionLoading ===
                                      `reactivate-${seat.seatCode}`
                                        ? "..."
                                        : "Reactivate"}
                                    </button>
                                  )
                                )}
                                <button
                                  onClick={() =>
                                    setInlineAction(
                                      isOpen && inlineAction.type === "extend"
                                        ? null
                                        : {
                                            seatCode: seat.seatCode,
                                            type: "extend",
                                            value: instantToWallClock(
                                              seat.expiresAt,
                                              TZ,
                                            ),
                                          },
                                    )
                                  }
                                  style={btnStyle("blue", true)}
                                >
                                  Extend
                                </button>
                                <button
                                  onClick={() =>
                                    setInlineAction(
                                      isOpen &&
                                        inlineAction.type === "reset-password"
                                        ? null
                                        : {
                                            seatCode: seat.seatCode,
                                            type: "reset-password",
                                            value: "",
                                          },
                                    )
                                  }
                                  style={btnStyle("secondary", true)}
                                >
                                  Reset Pwd
                                </button>
                              </div>
                            </td>
                          </tr>
                          {/* Inline action rows */}
                          {isOpen && inlineAction.type === "extend" && (
                            <tr>
                              <td
                                colSpan={9}
                                style={{
                                  padding: "8px 12px",
                                  background: "var(--bg-primary)",
                                  borderBottom: "1px solid var(--border-color)",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "12px",
                                    alignItems: "flex-start",
                                    flexWrap: "wrap",
                                  }}
                                >
                                  <DateTimeField
                                    label="New expiry"
                                    value={inlineAction.value}
                                    min={nowWallClock(TZ)}
                                    minMessage="now"
                                    presetBase={inlineAction.value}
                                    presets={["+1d", "+7d", "+30d"]}
                                    onChange={(value) =>
                                      setInlineAction((a) => ({ ...a, value }))
                                    }
                                  />
                                  <button
                                    onClick={() =>
                                      handleExtend(
                                        seat.seatCode,
                                        inlineAction.value,
                                      )
                                    }
                                    disabled={
                                      actionLoading ===
                                      `extend-${seat.seatCode}`
                                    }
                                    style={{
                                      ...btnStyle("primary", true),
                                      marginTop: "22px",
                                    }}
                                  >
                                    {actionLoading === `extend-${seat.seatCode}`
                                      ? "..."
                                      : "Save"}
                                  </button>
                                  <button
                                    onClick={() => setInlineAction(null)}
                                    style={{
                                      ...btnStyle("secondary", true),
                                      marginTop: "22px",
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                          {isOpen && inlineAction.type === "reset-password" && (
                            <tr>
                              <td
                                colSpan={9}
                                style={{
                                  padding: "8px 12px",
                                  background: "var(--bg-primary)",
                                  borderBottom: "1px solid var(--border-color)",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    gap: "8px",
                                    alignItems: "center",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      color: "var(--text-secondary)",
                                    }}
                                  >
                                    New password:
                                  </span>
                                  <input
                                    type="text"
                                    placeholder="e.g. coffee-morning-sunshine-blue"
                                    value={inlineAction.value}
                                    onChange={(e) =>
                                      setInlineAction((a) => ({
                                        ...a,
                                        value: e.target.value,
                                      }))
                                    }
                                    style={{ ...inputStyle, minWidth: "200px" }}
                                  />
                                  <button
                                    onClick={() =>
                                      handleResetPassword(
                                        seat.seatCode,
                                        inlineAction.value,
                                      )
                                    }
                                    disabled={
                                      actionLoading === `reset-${seat.seatCode}`
                                    }
                                    style={btnStyle("primary", true)}
                                  >
                                    {actionLoading === `reset-${seat.seatCode}`
                                      ? "..."
                                      : "Save"}
                                  </button>
                                  <button
                                    onClick={() => setInlineAction(null)}
                                    style={btnStyle("secondary", true)}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default FullAccessDemoAdmin;
